import { demoFunc } from './dees-terminal-view.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
} from '@design.estate/dees-element';
import type { Terminal } from 'xterm';
import type { FitAddon } from 'xterm-addon-fit';
import * as plugins from '../../00plugins.js';
import { themeDefaultStyles } from '../../00theme.js';
import { DeesServiceLibLoader } from '../../../services/index.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-terminal-view': DeesTerminalView;
  }
}

/** Detail of `terminal-input`: bytes the user typed, as a UTF-8 string. */
export interface ITerminalInputDetail {
  data: string;
}

/**
 * A terminal grid size. It is the detail of `terminal-resize`, fired after the
 * terminal refits its container, and the size `restore()` paints a snapshot at.
 */
export interface ITerminalResizeDetail {
  rows: number;
  cols: number;
}

/**
 * Detail of `terminal-error`: xterm could not be loaded, or the live terminal
 * refused output. The element stays usable and keeps buffering.
 */
export interface ITerminalErrorDetail {
  error: unknown;
}

/**
 * Upper bound on output buffered while no terminal is live, in bytes.
 * `Uint8Array` chunks count their `byteLength`, strings their UTF-16 memory
 * (two bytes per code unit). Beyond the bound the oldest chunks are dropped,
 * and the flush then emits RIS before the survivors so a truncated prefix is
 * never rendered as if it were contiguous output.
 */
export const maxPendingWriteBytes = 8 * 1024 * 1024;

/**
 * RIS (ESC c), a hard reset carried *inside* the write stream: xterm parses it
 * in order and routes it to the very same `Terminal.reset()` (InputHandler's
 * ESC-c handler calls `fullReset()`, which fires `onRequestReset`, which
 * `Terminal` handles with `reset()`), whereas calling `reset()` directly is
 * documented as out of band with the pending write queue.
 */
const RIS = '\x1bc';

/** Memory cost of one buffered chunk, in bytes. */
const chunkByteLength = (chunkArg: string | Uint8Array): number =>
  typeof chunkArg === 'string' ? chunkArg.length * 2 : chunkArg.byteLength;

/** Identity of a grid size, as compared when deciding whether to report a refit. */
const gridKey = (sizeArg: ITerminalResizeDetail): string => `${sizeArg.rows}x${sizeArg.cols}`;

/**
 * Grid bounds `restore()` accepts. xterm silently widens anything narrower than
 * two columns (a wide character needs two cells), which would paint a snapshot
 * at a size other than the one it was serialized at; a PTY window size is a
 * 16-bit field, so no real snapshot is larger than the upper bound.
 */
const minRestoreCols = 2;
const minRestoreRows = 1;
const maxRestoreGridSize = 0xffff;

const isGridDimension = (valueArg: number, minArg: number): boolean =>
  Number.isInteger(valueArg) && valueArg >= minArg && valueArg <= maxRestoreGridSize;

/**
 * One `restore()` call: the snapshot, the grid it was serialized at, and the
 * deferred whose promise is handed back to the caller. Its rejections are
 * lifecycle outcomes, not programming errors, so, as with `ready`, an
 * unawaited promise never surfaces as an unhandled rejection; a caller that
 * awaits it still observes the reason.
 */
class TerminalRestore {
  public readonly deferred = plugins.smartpromise.defer<void>();

  constructor(
    public readonly data: string | Uint8Array,
    public readonly size: ITerminalResizeDetail,
  ) {
    this.deferred.promise.catch(() => undefined);
  }
}

/** Everything buffered while no terminal is live, in replay order. */
interface IPendingOutput {
  /** The base of the writes; it resets everything that was buffered before it. */
  restore: TerminalRestore | undefined;
  writes: Array<string | Uint8Array>;
  /** Whether older output was dropped, so the replay has to reset before the writes. */
  dropped: boolean;
}

/**
 * Transport-agnostic xterm.js surface: the host pipes remote PTY output into
 * `write()` and receives keystrokes via `terminal-input` — no process,
 * runtime, or socket coupling. `terminal-resize` reports the grid size after
 * every refit so the host can negotiate a PTY size. Output that arrives while
 * no terminal is live is buffered up to `maxPendingWriteBytes` and replayed in
 * order on the next connection, so a replay may start before the element is
 * ready and survives being detached. `restore()` paints a serialized snapshot
 * invisibly at the grid it was taken at, then fits it to the container. xterm
 * loads through DeesServiceLibLoader; CSP-locked hosts can pre-register
 * bundled modules with `provideXtermModules()` to avoid the CDN.
 */
@customElement('dees-terminal-view')
export class DeesTerminalView extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  /** Font size in px for the terminal grid. */
  @property({ type: Number })
  accessor fontSize: number = 13;

  @property({ type: Boolean })
  accessor disabled: boolean = false;

  private terminal: Terminal | undefined;
  private fitAddon: FitAddon | undefined;
  private resizeObserver: ResizeObserver | undefined;
  private inputDisposable: { dispose(): void } | undefined;
  private lastReportedSize = '';
  // Output that arrives before xterm is live is replayed in order, bounded by
  // maxPendingWriteBytes and retained across a disconnect. A buffered restore
  // resets everything before it, so there is at most one and it is the base
  // of the chunks buffered after it; its bytes count towards the bound.
  private pendingRestore: TerminalRestore | undefined;
  private pendingWrites: Array<string | Uint8Array> = [];
  private pendingWriteBytes = 0;
  private pendingWritesDropped = false;
  private setupPromise: Promise<void> | undefined;
  /**
   * The restore handed to the live terminal whose snapshot is not parsed yet. While it is set the
   * grid belongs to the snapshot: the terminal stays invisible and nothing refits it.
   */
  private activeRestore: TerminalRestore | undefined;
  /**
   * Set only by an explicit `focus()` that arrived before the terminal existed. Nothing else sets
   * it, so a re-render, a write, or a resize can never move the caret into this element.
   */
  private focusWhenReady = false;
  private readyPending = true;
  private readyResolve: () => void = () => undefined;
  private readyReject: (reasonArg: unknown) => void = () => undefined;

  /**
   * Resolves once the terminal of the current connection is live; a fresh
   * promise is created for every connection. It rejects with the load error
   * when xterm cannot be loaded (`terminal-error` carries the same error), and
   * when the element is disconnected before the terminal came up.
   */
  public ready: Promise<void>;

  constructor() {
    super();
    this.ready = this.createReadyPromise();
    // The house lifecycle: startup runs on every connection with an abort
    // signal, garbage on every disconnection, so a reattached element is a
    // live surface again instead of a permanently dead one.
    this.registerStartupFunction(async (signalArg) => {
      await this.ensureTerminal(signalArg);
    });
    this.registerGarbageFunction(() => {
      this.teardownTerminal();
    });
  }

  public static styles = [
    themeDefaultStyles,
    css`
      :host {
        display: block;
        min-height: 0;
        height: 100%;
        background: var(--dees-color-bg-primary);
      }

      .terminalHost {
        width: 100%;
        height: 100%;
        padding: var(--dees-spacing-xs);
        box-sizing: border-box;
      }

      /*
       * A restore paints invisibly. The grid keeps its layout, so fitting still measures it, and
       * it stays focusable: visibility: hidden would blur the input surface and refuse focus().
       */
      .terminalHost.restoring {
        opacity: 0;
      }

      /* xterm.js styles (shadow DOM needs them locally) */
      .xterm {
        font-feature-settings: 'liga' 0;
        position: relative;
        user-select: none;
        -ms-user-select: none;
        -webkit-user-select: none;
      }

      .xterm.focus,
      .xterm:focus {
        outline: none;
      }

      .xterm .xterm-helpers {
        position: absolute;
        top: 0;
        z-index: 5;
      }

      .xterm .xterm-helper-textarea {
        padding: 0;
        border: 0;
        margin: 0;
        position: absolute;
        opacity: 0;
        left: -9999em;
        top: 0;
        width: 0;
        height: 0;
        z-index: -5;
        white-space: nowrap;
        overflow: hidden;
        resize: none;
      }

      .xterm .composition-view {
        background: var(--dees-color-bg-primary);
        color: var(--dees-color-text-primary);
        display: none;
        position: absolute;
        white-space: nowrap;
        z-index: 1;
      }

      .xterm .composition-view.active {
        display: block;
      }

      .xterm .xterm-viewport {
        background-color: var(--dees-color-bg-primary);
        overflow-y: scroll;
        cursor: default;
        position: absolute;
        right: 0;
        left: 0;
        top: 0;
        bottom: 0;
        scrollbar-width: thin;
        scrollbar-color: var(--dees-color-scrollbar-thumb) transparent;
      }

      .xterm .xterm-screen {
        position: relative;
      }

      .xterm .xterm-screen canvas {
        position: absolute;
        left: 0;
        top: 0;
      }

      .xterm .xterm-scroll-area {
        visibility: hidden;
      }

      .xterm-char-measure-element {
        display: inline-block;
        visibility: hidden;
        position: absolute;
        top: 0;
        left: -9999em;
        line-height: normal;
      }

      .xterm {
        cursor: text;
      }

      .xterm.enable-mouse-events {
        cursor: default;
      }

      .xterm.xterm-cursor-pointer {
        cursor: pointer;
      }

      .xterm.column-select.focus {
        cursor: crosshair;
      }

      .xterm .xterm-accessibility,
      .xterm .xterm-message {
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        right: 0;
        z-index: 10;
        color: transparent;
      }

      .xterm .live-region {
        position: absolute;
        left: -9999px;
        width: 1px;
        height: 1px;
        overflow: hidden;
      }

      .xterm-dim {
        opacity: 0.5;
      }

      .xterm-underline {
        text-decoration: underline;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`<div class="terminalHost"></div>`;
  }

  public connectedCallback(): Promise<void> {
    // A consumer awaiting readiness after reattaching must not be handed the
    // settled promise of the previous connection.
    if (!this.readyPending) this.ready = this.createReadyPromise();
    return super.connectedCallback();
  }

  /** Feed remote output into the terminal; safe to call before setup finishes. */
  public write(dataArg: string | Uint8Array): void {
    // While detached the terminal is about to be disposed, so output belongs in
    // the buffer rather than in a surface nobody will see again.
    if (this.terminal && this.isConnected) {
      this.writeToTerminal(this.terminal, dataArg);
      return;
    }
    this.bufferWrite(dataArg);
  }

  /**
   * Reset the screen and scrollback, e.g. before a full replay. Before the terminal exists it also
   * replaces a buffered `restore()`, whose promise then resolves as superseded.
   */
  public clear(): void {
    const terminal = this.terminal;
    if (terminal && this.isConnected) {
      this.writeToTerminal(terminal, RIS);
      return;
    }
    // Without a terminal the reset is expressed as a buffered sentinel, so the
    // flush of the next connection still performs it in the right order.
    this.discardPendingOutput();
    this.bufferWrite(RIS);
  }

  /**
   * Replace the terminal state with a serialized snapshot, e.g. from xterm's serialize addon, and
   * show it only once it is complete. A snapshot is exact only in a grid of the size it was
   * serialized at, so the terminal is fully reset (screen, scrollback, modes, alternate buffer),
   * set to `sizeArg`, fed `dataArg` while invisible, then fitted to its container and revealed.
   * `terminal-resize` fires at the end when the fitted grid differs from `sizeArg`, which is the
   * host's cue to resize the PTY so the application redraws at the new size. Until then
   * `currentSize` reports `sizeArg`, so the closing event, or this promise, is what a host
   * negotiates a PTY size on.
   *
   * The restore is part of the output stream: writes issued after it are applied after the
   * snapshot, in order, at the fitted size. Like `write()` it is safe before the terminal exists
   * and while the element is detached; a buffered restore replaces everything buffered before
   * it. The terminal keeps its focus and keeps forwarding input while the restore runs.
   *
   * The promise resolves once the snapshot is on screen, and also, without revealing anything,
   * when a newer `restore()` supersedes this one or `clear()` replaces it while it is buffered.
   * It rejects, without ever surfacing as an unhandled rejection, when the element is
   * disconnected while the snapshot is being parsed (the terminal is disposed with it, as with
   * any live output), when output buffered after it pushes it out of `maxPendingWriteBytes`, and
   * when xterm refuses the data (`terminal-error` carries the same error). Buffered output is
   * dropped oldest first and the restore is the oldest entry, so a buffered snapshot that alone
   * exceeds the bound is dropped by the next buffered write of any size. An invalid size is a
   * programming error: the promise rejects with a `RangeError` and nothing changes; unlike the
   * reasons above this one is not pre-handled, so a `restore()` nobody observes reports it as an
   * unhandled rejection.
   *
   * There is a fourth outcome: a restore that is still buffered when the element is disconnected
   * stays pending, because the snapshot survives the move like buffered output and is applied on
   * the next connection. A host that discards the element instead of reattaching it must not await
   * that promise.
   *
   * @param dataArg The serialized terminal state.
   * @param sizeArg The grid the snapshot was serialized at: integer `cols` in [2, 65535] and
   *   `rows` in [1, 65535].
   */
  public restore(dataArg: string | Uint8Array, sizeArg: ITerminalResizeDetail): Promise<void> {
    const { cols, rows } = sizeArg;
    if (!isGridDimension(cols, minRestoreCols) || !isGridDimension(rows, minRestoreRows)) {
      return Promise.reject(
        new RangeError(
          `restore() needs integer cols in [${minRestoreCols}, ${maxRestoreGridSize}] and rows in ` +
            `[${minRestoreRows}, ${maxRestoreGridSize}], got ${cols}x${rows}`,
        ),
      );
    }
    const restore = new TerminalRestore(dataArg, { cols, rows });
    const terminal = this.terminal;
    if (terminal && this.isConnected) {
      this.startRestore(terminal, restore);
    } else {
      this.discardPendingOutput();
      this.pendingRestore = restore;
      this.pendingWriteBytes = chunkByteLength(dataArg);
    }
    return restore.deferred.promise;
  }

  /**
   * Moves the caret into the terminal's input surface, so the next keystroke goes to the pty
   * rather than to whatever the page focused last. This is the call a host makes when the user
   * selects a terminal in a list.
   *
   * The terminal is created asynchronously — the xterm modules are loaded on first connection —
   * so a call that arrives before it exists is remembered and applied once it is ready, exactly
   * once. The same applies to an element that is focused while detached and then reattached: the
   * pending request is dropped on disconnect, because by then the user's intent has expired, and
   * a fresh `focus()` after the move is what a host should issue.
   *
   * Only this method arms that behaviour; re-renders, buffered output and background updates
   * never steal focus.
   */
  public focus(optionsArg?: FocusOptions): void {
    const terminal = this.terminal;
    if (terminal && this.isConnected) {
      this.focusWhenReady = false;
      terminal.focus();
      return;
    }
    // Preventing scroll is a hint for the eventual focus, not a reason to skip it.
    void optionsArg;
    this.focusWhenReady = true;
  }

  /** True while an explicit `focus()` is waiting for the terminal to come up. */
  public get focusPending(): boolean {
    return this.focusWhenReady;
  }

  /**
   * Current grid size, or undefined while no terminal is live. While a snapshot is being parsed
   * this is the grid the snapshot was serialized at; the closing `terminal-resize`, or the promise
   * `restore()` returned, is the grid to negotiate a PTY size on.
   */
  public get currentSize(): ITerminalResizeDetail | undefined {
    if (!this.terminal) return undefined;
    return { rows: this.terminal.rows, cols: this.terminal.cols };
  }

  private createReadyPromise(): Promise<void> {
    this.readyPending = true;
    const promise = new Promise<void>((resolveArg, rejectArg) => {
      this.readyResolve = () => {
        this.readyPending = false;
        resolveArg();
      };
      this.readyReject = (reasonArg) => {
        this.readyPending = false;
        rejectArg(reasonArg);
      };
    });
    // A readiness promise nobody awaits must never surface as an unhandled
    // rejection; consumers that do await it still observe the reason.
    promise.catch(() => undefined);
    return promise;
  }

  private bufferWrite(chunkArg: string | Uint8Array): void {
    this.pendingWrites.push(chunkArg);
    this.pendingWriteBytes += chunkByteLength(chunkArg);
    // Oldest first, and never the newest entry: a buffered restore is older
    // than every chunk, so it goes before any of them.
    while (
      this.pendingWriteBytes > maxPendingWriteBytes &&
      this.pendingWrites.length > (this.pendingRestore ? 0 : 1)
    ) {
      const droppedRestore = this.pendingRestore;
      if (droppedRestore) {
        this.pendingRestore = undefined;
        this.pendingWriteBytes -= chunkByteLength(droppedRestore.data);
        droppedRestore.deferred.reject(
          new Error(
            'dees-terminal-view dropped a buffered restore: the output buffered after it exceeded maxPendingWriteBytes',
          ),
        );
      } else {
        this.pendingWriteBytes -= chunkByteLength(this.pendingWrites.shift()!);
      }
      this.pendingWritesDropped = true;
    }
  }

  /** Hands the buffered output over and leaves the buffer empty. */
  private takePendingOutput(): IPendingOutput {
    const output: IPendingOutput = {
      restore: this.pendingRestore,
      writes: this.pendingWrites,
      dropped: this.pendingWritesDropped,
    };
    this.pendingRestore = undefined;
    this.pendingWrites = [];
    this.pendingWriteBytes = 0;
    this.pendingWritesDropped = false;
    return output;
  }

  /** Empties the buffer ahead of a reset; a buffered restore is superseded by it. */
  private discardPendingOutput(): void {
    this.takePendingOutput().restore?.deferred.resolve();
  }

  private flushPendingWrites(terminalArg: Terminal): void {
    const { restore, writes, dropped } = this.takePendingOutput();
    if (restore && !this.startRestore(terminalArg, restore)) return;
    if (dropped && !this.writeToTerminal(terminalArg, RIS)) return;
    for (const chunk of writes) {
      if (!this.writeToTerminal(terminalArg, chunk)) return;
    }
  }

  /**
   * Queues a restore into xterm's write stream. Resetting and resizing happen in the callback of
   * the reset, so they take effect exactly at the restore's position in the stream, and the
   * restore ends in the callback of its snapshot, before xterm parses the next chunk.
   */
  private startRestore(terminalArg: Terminal, restoreArg: TerminalRestore): boolean {
    this.activeRestore?.deferred.resolve();
    this.activeRestore = restoreArg;
    this.terminalHost?.classList.add('restoring');
    try {
      terminalArg.write(RIS, () => this.applyRestoreSize(terminalArg, restoreArg));
      terminalArg.write(restoreArg.data, () => this.finishRestore(restoreArg));
      return true;
    } catch (error) {
      // xterm refused the snapshot at its flow-control watermark. A reset it did accept is parsed
      // later, finds the restore inactive and leaves the grid alone.
      this.endRestore();
      this.reportError(error);
      restoreArg.deferred.reject(error);
      return false;
    }
  }

  private applyRestoreSize(terminalArg: Terminal, restoreArg: TerminalRestore): void {
    if (this.activeRestore !== restoreArg) return;
    terminalArg.resize(restoreArg.size.cols, restoreArg.size.rows);
    // The host serialized the snapshot at this grid, so it is the size the host already knows:
    // the closing refit reports only a difference from it.
    this.lastReportedSize = gridKey(restoreArg.size);
  }

  private finishRestore(restoreArg: TerminalRestore): void {
    // A superseded restore was settled when the newer one started.
    if (this.activeRestore !== restoreArg) return;
    this.endRestore();
    restoreArg.deferred.resolve();
  }

  /**
   * Hands the grid back to the container: one refit, then the terminal is revealed. The refit
   * dispatches `terminal-resize` synchronously, and a restore its listener starts keeps the
   * terminal hidden.
   */
  private endRestore(): void {
    this.activeRestore = undefined;
    this.refit();
    if (!this.activeRestore) this.terminalHost?.classList.remove('restoring');
  }

  /** xterm throws at its own flow-control watermark; that has to reach the host. */
  private writeToTerminal(terminalArg: Terminal, chunkArg: string | Uint8Array): boolean {
    try {
      terminalArg.write(chunkArg);
      return true;
    } catch (error) {
      this.reportError(error);
      return false;
    }
  }

  private reportError(errorArg: unknown): void {
    if (this.readyPending) this.readyReject(errorArg);
    const detail: ITerminalErrorDetail = { error: errorArg };
    this.dispatchEvent(
      new CustomEvent('terminal-error', { detail, bubbles: true, composed: true }),
    );
  }

  private get terminalHost(): HTMLElement | null {
    return this.shadowRoot?.querySelector<HTMLElement>('.terminalHost') ?? null;
  }

  private ensureTerminal(signalArg: AbortSignal): Promise<void> {
    if (this.terminal) return Promise.resolve();
    if (!this.setupPromise) this.setupPromise = this.setupTerminal(signalArg);
    return this.setupPromise;
  }

  private async setupTerminal(signalArg: AbortSignal): Promise<void> {
    try {
      await this.createTerminal(signalArg);
    } catch (error) {
      this.reportError(error);
    } finally {
      // Nothing came up: release the guard so the next connection retries.
      if (!this.terminal) this.setupPromise = undefined;
    }
  }

  private async createTerminal(signalArg: AbortSignal): Promise<void> {
    await this.updateComplete;
    const libLoader = DeesServiceLibLoader.getInstance();
    const [xterm, fit] = await Promise.all([
      libLoader.loadXterm(),
      libLoader.loadXtermFitAddon(),
    ]);
    // The load was awaited: the element may have been detached meanwhile.
    if (signalArg.aborted || !this.isConnected) return;
    const host = this.terminalHost;
    if (!host) return;
    const computed = getComputedStyle(this);
    const themeColor = (nameArg: string, fallbackArg: string): string => {
      const value = computed.getPropertyValue(nameArg).trim();
      return value || fallbackArg;
    };
    const terminal = new xterm.Terminal({
      fontSize: this.fontSize,
      fontFamily: themeColor('--dees-font-family-mono', 'monospace'),
      cursorBlink: true,
      allowProposedApi: true,
      scrollback: 10_000,
      theme: {
        background: themeColor('--dees-color-bg-primary', '#111111'),
        foreground: themeColor('--dees-color-text-primary', '#e6e6e6'),
        cursor: themeColor('--dees-color-accent-primary', '#e6e6e6'),
      },
    });
    let fitAddon: FitAddon;
    try {
      fitAddon = new fit.FitAddon();
      terminal.loadAddon(fitAddon);
      terminal.open(host);
    } catch (error) {
      // The element does not own this instance yet, so teardown cannot reach
      // it; a half-opened terminal would otherwise leak with its DOM attached.
      try {
        terminal.dispose();
      } catch {
        // Disposing a half-opened terminal must not mask the real failure.
      }
      throw error;
    }
    this.terminal = terminal;
    this.fitAddon = fitAddon;
    this.inputDisposable = terminal.onData((dataArg) => {
      if (this.disabled) return;
      const detail: ITerminalInputDetail = { data: dataArg };
      this.dispatchEvent(
        new CustomEvent('terminal-input', { detail, bubbles: true, composed: true }),
      );
    });
    this.flushPendingWrites(terminal);
    this.refit();
    this.resizeObserver = new ResizeObserver(() => this.refit());
    this.resizeObserver.observe(host);
    // An explicit focus() that arrived before the terminal existed is honoured now, once.
    if (this.focusWhenReady) {
      this.focusWhenReady = false;
      terminal.focus();
    }
    this.readyResolve();
  }

  private refit(): void {
    // A restore owns the grid until its snapshot is parsed and refits once when it ends.
    if (!this.terminal || !this.fitAddon || this.activeRestore) return;
    try {
      this.fitAddon.fit();
    } catch {
      return;
    }
    const size = gridKey(this.terminal);
    if (size === this.lastReportedSize) return;
    this.lastReportedSize = size;
    const detail: ITerminalResizeDetail = {
      rows: this.terminal.rows,
      cols: this.terminal.cols,
    };
    this.dispatchEvent(
      new CustomEvent('terminal-resize', { detail, bubbles: true, composed: true }),
    );
  }

  private teardownTerminal(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.inputDisposable?.dispose();
    this.inputDisposable = undefined;
    // A disposed terminal never parses the rest of its queue, so a restore in flight would
    // otherwise stay pending forever; the snapshot is lost with the terminal, like live output.
    const interruptedRestore = this.activeRestore;
    this.activeRestore = undefined;
    this.terminalHost?.classList.remove('restoring');
    this.terminal?.dispose();
    this.terminal = undefined;
    this.fitAddon = undefined;
    // Instance state of the finished connection: a reattached element has to
    // re-announce its grid and may retry a load that failed.
    this.lastReportedSize = '';
    this.setupPromise = undefined;
    // A focus request belongs to the connection it was made in; a reattached element must not
    // grab the caret from wherever the user moved it meanwhile.
    this.focusWhenReady = false;
    // Buffered output deliberately survives, so a replay that arrived while the
    // element was detached still reaches the next connection.
    if (this.readyPending) {
      this.readyReject(
        new Error('dees-terminal-view was disconnected before the terminal became ready'),
      );
    }
    interruptedRestore?.deferred.reject(
      new Error('dees-terminal-view was disconnected before the restore completed'),
    );
  }
}
